/**
 * Company data with no imports — the office and the service list.
 *
 * Kept free of path aliases and JSON imports so the Node build scripts can import it
 * directly (see scripts/generate-llms.mjs). The app reads these through `site.js`.
 */

export const OFFICE = {
  /** Full registered address on one line — used where a single string is needed. */
  address:
    "M/S SNAPX TECHNOLOGIES LLP, Shushila Shadan, Kamla Sharan Colony, NH-83, Nathupur Rd, beside S.D.V. Public School, Kurthoul, Patna, Nathupur, Bihar 804453",
  /** Same address broken for display, so it reads as a postal block rather than a run-on line. */
  addressLines: [
    "M/S SNAPX TECHNOLOGIES LLP",
    "Shushila Shadan, Kamla Sharan Colony",
    "NH-83, Nathupur Road",
    "Beside S.D.V. Public School, Kurthoul",
    "Patna, Nathupur, Bihar 804453",
  ],
  /**
   * Google Business Profile place link, for the "Get directions" action.
   *
   * The previous share.google link resolved to a Google *search* page for the
   * business rather than its place on the map, so the directions button did not
   * give directions. This one opens the place itself. Same listing either way —
   * both carry knowledge-graph id /g/11q9475qhf.
   *
   * Resolves to: SNAPX TECHNOLOGIES, 25.5555642, 85.1217201.
   */
  mapsUrl: "https://maps.app.goo.gl/BswrvWnHqwzLauWXA",
  /** Coordinates of the registered office, for GeoCoordinates schema. */
  geo: { latitude: 25.5555642, longitude: 85.1217201 },
  hours: "Mon–Fri 8 AM–4 PM · Sat 8 AM–1 PM",
  /** Structured form, used for PostalAddress schema. */
  postal: {
    name: "M/S SNAPX TECHNOLOGIES LLP",
    streetAddress:
      "Shushila Shadan, Kamla Sharan Colony, NH-83, Nathupur Rd, beside S.D.V. Public School, Kurthoul, Nathupur",
    addressLocality: "Patna",
    addressRegion: "Bihar",
    postalCode: "804453",
    addressCountry: "IN",
  },
};

/**
 * Statutory identifiers.
 *
 * The site claimed Startup India recognition on the About page and gave no number
 * for it, and published no LLPIN, GST or Udyam registration anywhere — so a
 * visitor deciding whether to wire money to a small Patna firm had nothing to
 * check. Every value here is verifiable in a public registry, which is the point:
 * it answers "are these people real" without asking anyone to take our word.
 *
 * Section 21 of the LLP Act 2008 requires an LLP's invoices and official
 * correspondence to carry its name, registered office, LLPIN and a statement that
 * it is registered with limited liability. Putting the same block in the footer
 * and on the legal page is the ordinary convention among Indian companies, and it
 * keeps the website saying what the invoices say.
 *
 * DPIIT recognition and Startup India are one certificate under two names, so it
 * is listed once. Listing both looks like padding to the kind of reader who
 * verifies one of them.
 */
export const REGISTRATIONS = [
  { label: "LLPIN", value: "AAX-6898", note: "Registered with the Ministry of Corporate Affairs" },
  { label: "GSTIN", value: "10AEMFS0052G1ZH", note: "Bihar" },
  { label: "Udyam", value: "UDYAM-BR-26-0088932", note: "MSME registered" },
  { label: "DPIIT", value: "DIPP85668", note: "DPIIT-recognised startup (Startup India)" },
];

/** "LLPIN AAX-6898 · GSTIN …" — for a footer bar with one line to spare. */
export const registrationLine = REGISTRATIONS.map((r) => `${r.label} ${r.value}`).join(" · ");

/** Looked up by label so a caller never re-types a number. */
export const registration = (label) => REGISTRATIONS.find((r) => r.label === label)?.value;

/** Required on correspondence, and the plainest statement of what the entity is. */
export const LIABILITY_STATEMENT = "Registered with limited liability.";

/**
 * The name on the bank account — exactly as the bank holds it, and the only name a
 * payment to us can carry. Note it has no "M/S": that is an honorific people put in
 * front of a firm's name, and it is not part of the account.
 */
export const BANKING_NAME = "SNAPX TECHNOLOGIES LLP";

/**
 * Who the money goes to, and who it does not.
 *
 * Another business trades under the name Snapx, and people who have been charged by
 * it search for its refund policy, land on these pages, and ring us instead. We can
 * do nothing for them — we do not hold their money and cannot see or reverse a charge
 * made by someone else — and every such call costs a working day's patience on both
 * ends. So the pages a confused visitor actually reaches (the legal documents, the
 * contact page) say, plainly and first, which account is ours.
 *
 * The wording is deliberately about us and not about them. It does not name the other
 * business, describe how it operates or suggest it has done anything wrong: we know
 * what we receive, we do not know what they do, and a published accusation we cannot
 * prove is a liability of its own. "We are not connected with it" is true, and enough.
 *
 * The "never auto-debit" line is a public commitment, not a description of current
 * practice: if a service is ever billed through a mandate, this sentence stops being true
 * and has to change before the first mandate is set up, not after.
 *
 * It is kept out of titles and meta descriptions on purpose. A search result that
 * leads with a disclaimer reads defensively to the clients who are looking for us.
 * The notice lives on the page they land on; the snippet stays about what we do.
 */
export const PAYMENT_NOTICE = {
  heading: "Before you contact us: are you looking for a different SNAPX?",
  lead:
    "SNAPX TECHNOLOGIES LLP is a website, software and digital-services company in Patna, Bihar. " +
    "We are not connected with any other business, app or service that uses the name SNAPX or a similar name.",
  points: [
    `Every payment to us goes into one bank account, held in the name ${BANKING_NAME}. We do not accept payments into any other account.`,
    "We never take money from anyone's account automatically: no auto-debit, standing instruction or e-mandate. Every payment to us is one you choose to make.",
    `Check the name on your bank or UPI statement. If it does not read ${BANKING_NAME}, the money did not come to us — and we cannot see, stop, refund or reverse it.`,
    "If you were charged by another app or service, please contact that business directly, and your bank or UPI app, quoting the merchant name shown on your statement.",
  ],
  /** Appended by the page, which knows the phone and email; this file imports nothing. */
  ifYouPaidUs:
    `If you did pay ${BANKING_NAME}, write to us or call with the transaction reference (UTR), the date and the amount, and our refund policy applies.`,
  /** The short form, for the contact page, beside the phone number. */
  short:
    `We receive payments only as ${BANKING_NAME}, and we never deduct money automatically. If your bank statement shows a different name, it is not us, and we cannot see or reverse it.`,
};

export const BASE_SERVICES = [
  {
    slug: "product-development",
    index: "01",
    name: "Product Development",
    tier: "technology",
    accent: "#3B82F6",
    tagline: "From idea to a product people pay for.",
    summary:
      "We take products from napkin sketch to production — MVP scoping, architecture, full-stack engineering and launch. The same way we built EduAnant, our own school management product.",
    deliverables: ["MVP scoping & validation", "Product architecture", "Full-stack engineering", "SaaS platforms & dashboards", "Launch & iteration"],
    outcome: "A real product in users' hands — built by a team that runs its own SaaS in production.",
  },
  {
    slug: "web-development",
    index: "02",
    name: "Web Development",
    tier: "technology",
    accent: "#4ADE80",
    tagline: "Fast, honest engineering. No duct tape.",
    summary:
      "Production-grade websites and web apps — marketing sites that score green on Lighthouse and platforms that stay up when your launch does.",
    deliverables: ["Marketing websites", "Web applications", "CMS & headless setups", "API development", "Performance engineering"],
    outcome: "Sub-two-second loads and codebases your next team can pick up without an archaeology degree.",
  },
  {
    slug: "mobile-apps",
    index: "03",
    name: "Mobile Apps",
    tier: "technology",
    accent: "#38BDF8",
    tagline: "Apps that earn their place on the home screen.",
    summary:
      "iOS and Android apps built for Indian network realities — offline-tolerant, light on data, and designed for the mid-range phones your users actually carry.",
    deliverables: ["iOS & Android apps", "Cross-platform builds", "Offline-first architecture", "App store launch", "Analytics & crash tooling"],
    outcome: "Apps that work on patchy 4G in a moving bus — because that is where your users are.",
  },
  {
    slug: "e-commerce",
    index: "04",
    name: "E-commerce",
    tier: "technology",
    accent: "#FB923C",
    tagline: "Storefronts that sell while you sleep.",
    summary:
      "High-performance e-commerce builds — from D2C storefronts to full marketplaces — engineered for conversion on Indian connections and Indian payment habits.",
    deliverables: ["D2C storefronts", "Marketplace builds", "Payment & logistics integration", "Conversion optimisation", "Inventory & order systems"],
    outcome: "A store that loads fast, builds trust, and turns browsers into repeat buyers.",
  },
  {
    slug: "ui-ux-design",
    index: "05",
    name: "UI/UX Design",
    tier: "technology",
    accent: "#FBCFE8",
    tagline: "Interfaces people feel before they understand.",
    summary:
      "Research-driven product design — from user interviews and journey maps to pixel-perfect design systems your engineers will actually thank you for.",
    deliverables: ["UX research & audits", "Wireframes & prototypes", "Design systems", "Product UI design", "Usability testing"],
    outcome: "Products that convert because they were designed around evidence, not opinion.",
  },
  {
    slug: "data-analytics",
    index: "06",
    name: "Data Analytics",
    tier: "technology",
    accent: "#34D399",
    tagline: "Your numbers, finally talking sense.",
    summary:
      "Dashboards, pipelines and reporting that turn scattered business data into decisions you can defend in a boardroom.",
    deliverables: ["Analytics dashboards", "Data pipelines", "Business reporting", "Tracking & attribution setup", "Forecasting models"],
    outcome: "One dashboard that answers 'how are we doing?' before anyone asks.",
  },
  {
    slug: "cloud-solutions",
    index: "07",
    name: "Cloud Solutions",
    tier: "technology",
    accent: "#818CF8",
    tagline: "Infrastructure that scales before you feel the strain.",
    summary:
      "Cloud architecture, migration and DevOps — so your platform stays fast on launch day and affordable on quiet ones.",
    deliverables: ["Cloud architecture", "Migration & modernisation", "CI/CD & DevOps", "Monitoring & cost control", "Backup & disaster recovery"],
    outcome: "Infrastructure that quietly absorbs your growth instead of interrupting it.",
  },
  {
    slug: "cyber-security",
    index: "08",
    name: "Cyber Security",
    tier: "technology",
    accent: "#F87171",
    tagline: "Quiet protection for loud ambitions.",
    summary:
      "Security audits, hardening and monitoring that protect your customer data and your reputation — before either is tested.",
    deliverables: ["Security audits", "Application hardening", "Vulnerability scanning", "Access & compliance setup", "Incident response planning"],
    outcome: "Systems your customers can trust — with the evidence to prove it.",
  },
  {
    slug: "digital-marketing",
    index: "09",
    name: "Digital Marketing",
    tier: "growth",
    accent: "#FBBF24",
    tagline: "Growth you can read on a dashboard.",
    summary:
      "Full-funnel performance marketing — creative, media and analytics under one roof, so nothing gets lost between agency and client.",
    deliverables: ["Performance advertising", "SEO & content", "Social media systems", "Email & WhatsApp flows", "Attribution & analytics"],
    outcome: "A funnel where every rupee is tracked from first impression to repeat purchase.",
  },
  {
    slug: "branding",
    index: "10",
    name: "Branding",
    tier: "growth",
    accent: "#F43F5E",
    tagline: "Identities bold enough to be remembered.",
    summary:
      "Strategy-first brand identity — naming, logo systems, typography and voice that make you unmistakable in a crowded Indian market.",
    deliverables: ["Brand strategy & building", "Logo & visual identity", "Packaging & print collateral", "Digital graphics", "Brand guidelines"],
    outcome: "A brand your customers can describe in one sentence — and recognise in one second.",
  },
  {
    slug: "motion-graphics",
    index: "11",
    name: "Motion Graphics",
    tier: "growth",
    accent: "#D946EF",
    tagline: "Stories that move. Literally.",
    summary:
      "Product explainers, brand films and interface motion that make complex things feel obvious in under sixty seconds.",
    deliverables: ["Product explainer films", "Brand motion language", "UI animation", "Social video systems", "Lottie & web animation"],
    outcome: "Explainers your sales team plays in every pitch, and motion that makes your product feel alive.",
  },
  {
    slug: "finance-gst",
    index: "12",
    name: "Finance, Tax & GST",
    tier: "growth",
    accent: "#14B8A6",
    tagline: "Compliance that never keeps you up at night.",
    summary:
      "GST filing, bookkeeping and financial compliance for growing businesses — handled quietly, correctly and always on time.",
    deliverables: ["GST registration & filing", "Bookkeeping & CA assistance", "Tax advisory & planning", "Incorporation & bank assistance", "Auditing & financial reporting"],
    outcome: "Clean books, on-time filings, and a finance function that scales with you.",
  },
];
